import React from 'react';
import { Check, Layout, Sparkles, Minimize2, PartyPopper, Crown } from 'lucide-react';
import { TEMPLATES, type ThemeTemplate } from '@/lib/landing-theme-constants';

interface TemplatePickerProps {
  value: string;
  onChange: (templateId: string) => void;
}

const TEMPLATE_ICONS: Record<string, React.FC<{ className?: string }>> = {
  classic: Layout,
  elegant: Crown,
  modern: Sparkles,
  festive: PartyPopper,
  minimal: Minimize2,
};

const TemplatePreview: React.FC<{ template: ThemeTemplate }> = ({ template }) => {
  const Icon = TEMPLATE_ICONS[template.id] || Layout;
  return (
    <div className="h-20 rounded-md bg-muted/50 border border-border/50 flex flex-col items-center justify-center gap-1.5 mb-2">
      <Icon className="h-6 w-6 text-muted-foreground" />
      <span className="text-[10px] text-muted-foreground font-medium">{template.name}</span>
    </div>
  );
};

export const TemplatePicker: React.FC<TemplatePickerProps> = ({ value, onChange }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {TEMPLATES.map((template) => {
        const isSelected = value === template.id;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onChange(template.id)}
            className={`relative rounded-lg border-2 p-3 text-left transition-all hover:shadow-md ${
              isSelected
                ? 'border-primary ring-2 ring-primary/20 shadow-sm'
                : 'border-border hover:border-primary/40'
            }`}
          >
            {isSelected && (
              <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                <Check className="h-3 w-3" />
              </div>
            )}
            <TemplatePreview template={template} />
            <div className="text-xs font-semibold text-foreground">{template.name}</div>
            <div className="text-[10px] text-muted-foreground leading-tight mt-0.5 line-clamp-2">
              {template.description}
            </div>
          </button>
        );
      })}
    </div>
  );
};
